// src/components/map/LegendPropertiesEditor.jsx
"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { Save, RotateCcw, EyeOff, Eye } from "lucide-react";
import PropTypes from "prop-types";

/**
 * LegendPropertiesEditor - Comprehensive component for editing legend properties
 * Similar structure to LayerPropertiesEditor/ColorBreakEditor but focused on legend editing
 */
const LegendPropertiesEditor = ({
  isOpen,
  onClose,
  layerConfig,
  onConfigChange,
  onSave,
  onPreview, // Added for live preview support
}) => {
  // State for legend properties
  const [legendTitle, setLegendTitle] = useState("");
  const [colorLabel, setColorLabel] = useState("");
  const [sizeLabel, setSizeLabel] = useState("");
  const [prefix, setPrefix] = useState("");
  const [suffix, setSuffix] = useState("");
  const [decimals, setDecimals] = useState(2);
  const [hideLegend, setHideLegend] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [legendItems, setLegendItems] = useState([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  // Store original values for reset functionality
  const originalValuesRef = useRef({});

  // Determine legend type based on config
  const getLegendType = (config) => {
    if (!config) return "unknown";
    if (config.type === "pipe" || config.statusColors) return "pipeline";
    if (config.classBreakInfos && config.classBreakInfos.length > 0) return "class-breaks";
    // Check for true 2-variable custom map (has BOTH color and size breaks)
    if (config.type === "custom" &&
        config.colorClassBreakInfos && config.colorClassBreakInfos.length > 0 &&
        config.sizeClassBreakInfos && config.sizeClassBreakInfos.length > 0) {
      return "custom-dual";
    }
    if (config.dotValue !== undefined) return "dot-density";
    if (config.customData?.data) return "point";
    return "unknown";
  };

  const legendType = getLegendType(layerConfig);

  // Check if this is truly a 2-variable map for showing color/size labels
  const is2VariableMap = legendType === "custom-dual" &&
    layerConfig?.valueColumn1 &&
    layerConfig?.valueColumn2;

  console.log('[LegendPropertiesEditor] Map type detection:', {
    legendType,
    is2VariableMap,
    configType: layerConfig?.type,
    hasColorBreaks: !!(layerConfig?.colorClassBreakInfos?.length),
    hasSizeBreaks: !!(layerConfig?.sizeClassBreakInfos?.length),
    hasClassBreaks: !!(layerConfig?.classBreakInfos?.length),
  });

  // Format value based on current formatting settings
  const formatValue = (value, includeSuffix = true) => {
    if (value === undefined || value === null || typeof value !== "number")
      return value || "";

    const numValue = Number(value);
    if (isNaN(numValue)) return value;

    let formattedValue = "";

    // Apply decimal formatting
    if (decimals === 0) {
      formattedValue = Math.round(numValue).toLocaleString();
    } else {
      formattedValue = numValue.toFixed(decimals);
    }

    // Apply prefix and suffix (no automatic spacing - users add spaces if needed)
    return `${prefix}${formattedValue}${includeSuffix && suffix ? suffix : ''}`;
  };

  // Extract legend items from config
  const extractLegendItems = useCallback((config) => {
    if (!config) return [];

    const type = getLegendType(config);
    const items = [];

    if (type === "class-breaks" && config.classBreakInfos) {
      config.classBreakInfos.forEach((breakInfo, index) => {
        items.push({
          id: index,
          label: breakInfo.label || `Break ${index + 1}`,
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          color: breakInfo.symbol?.color,
          editable: true,
        });
      });
    } else if (type === "pipeline" && config.statusColors) {
      Object.keys(config.statusColors).forEach((status, index) => {
        if (status !== "default") {
          items.push({
            id: index,
            label: status,
            color: config.statusColors[status],
            editable: true,
          });
        }
      });
    } else if (type === "custom-dual" && config.colorClassBreakInfos) {
      config.colorClassBreakInfos.forEach((breakInfo, index) => {
        items.push({
          id: index,
          label: breakInfo.label || `Break ${index + 1}`,
          minValue: breakInfo.minValue,
          maxValue: breakInfo.maxValue,
          color: breakInfo.symbol?.color,
          editable: true,
        });
      });
    } else if (type === "dot-density" && config.attributes) {
      config.attributes.forEach((attr, index) => {
        items.push({
          id: index,
          label: attr.label || attr.field,
          color: attr.color,
          editable: true,
        });
      });
    }

    return items;
  }, []);

  // Initialize state from layerConfig
  useEffect(() => {
    if (layerConfig && isOpen) {
      console.log('[LegendPropertiesEditor] Initializing with config:', layerConfig);

      // Get current title - check multiple possible locations
      const title = layerConfig.title ||
                    layerConfig.name ||
                    layerConfig.layerTitle ||
                    "";
      console.log('[LegendPropertiesEditor] Extracted title:', title);
      setLegendTitle(title);

      // Get color and size labels for 2-variable maps
      const newColorLabel = layerConfig.colorLabel || layerConfig.valueColumn1 || "";
      const newSizeLabel = layerConfig.sizeLabel || layerConfig.valueColumn2 || "";
      setColorLabel(newColorLabel);
      setSizeLabel(newSizeLabel);

      // Get current format information from valueFormat object
      const valueFormat = layerConfig.valueFormat || {};
      const newPrefix = valueFormat.prefix || "";
      const newSuffix = valueFormat.suffix || "";
      const newDecimals =
        valueFormat.decimals !== undefined ? valueFormat.decimals : 2;

      setPrefix(newPrefix);
      setSuffix(newSuffix);
      setDecimals(newDecimals);

      // Get hide legend state
      const newHideLegend = layerConfig.hideLegend || false;
      setHideLegend(newHideLegend);

      // Extract legend items
      const items = extractLegendItems(layerConfig);
      setLegendItems(items);

      // Store original values for reset
      originalValuesRef.current = {
        title,
        colorLabel: newColorLabel,
        sizeLabel: newSizeLabel,
        prefix: newPrefix,
        suffix: newSuffix,
        decimals: newDecimals,
        hideLegend: newHideLegend,
        items: JSON.parse(JSON.stringify(items)),
      };

      setHasUnsavedChanges(false);
    }
  }, [layerConfig, isOpen, extractLegendItems]);

  // Track changes
  useEffect(() => {
    if (originalValuesRef.current.title !== undefined) {
      const hasChanges =
        legendTitle !== originalValuesRef.current.title ||
        colorLabel !== originalValuesRef.current.colorLabel ||
        sizeLabel !== originalValuesRef.current.sizeLabel ||
        prefix !== originalValuesRef.current.prefix ||
        suffix !== originalValuesRef.current.suffix ||
        decimals !== originalValuesRef.current.decimals ||
        hideLegend !== originalValuesRef.current.hideLegend ||
        JSON.stringify(legendItems) !==
          JSON.stringify(originalValuesRef.current.items);
      setHasUnsavedChanges(hasChanges);
    }
  }, [legendTitle, colorLabel, sizeLabel, prefix, suffix, decimals, hideLegend, legendItems]);

  // Track if format settings have been initialized
  const formatInitializedRef = useRef(false);
  const lastFormatRef = useRef({ prefix: '', suffix: '', decimals: 2 });

  // Auto-regenerate labels when format settings change
  useEffect(() => {
    // Skip if not initialized yet or no legend items
    if (!formatInitializedRef.current || legendItems.length === 0) {
      // Mark as initialized after first render with items
      if (legendItems.length > 0) {
        formatInitializedRef.current = true;
        lastFormatRef.current = { prefix, suffix, decimals };
      }
      return;
    }

    // Check if format actually changed
    const formatChanged =
      lastFormatRef.current.prefix !== prefix ||
      lastFormatRef.current.suffix !== suffix ||
      lastFormatRef.current.decimals !== decimals;

    if (!formatChanged) return;

    // Update last format
    lastFormatRef.current = { prefix, suffix, decimals };

    // Only regenerate for class-breaks type legends
    if (legendType !== "class-breaks" && legendType !== "custom-dual") return;

    // Auto-regenerate labels
    console.log('[LegendPropertiesEditor] Auto-regenerating labels due to format change');

    const generateLabel = (minValue, maxValue, isFirst, isLast, totalItems) => {
      const formatNum = (value) => {
        if (value === undefined || value === null || typeof value !== "number") return "";
        const numValue = Number(value);
        if (isNaN(numValue)) return "";
        const dec = parseInt(decimals, 10);
        if (dec === 0) {
          return `${prefix}${Math.round(numValue).toLocaleString()}${suffix || ''}`;
        } else {
          return `${prefix}${numValue.toLocaleString(undefined, { minimumFractionDigits: dec, maximumFractionDigits: dec })}${suffix || ''}`;
        }
      };

      if (totalItems === 1 || minValue === maxValue) return formatNum(minValue);
      if (isFirst) return `Less than ${formatNum(maxValue)}`;
      if (isLast) return `${formatNum(minValue)} or more`;
      return `${formatNum(minValue)} - ${formatNum(maxValue)}`;
    };

    setLegendItems((prevItems) =>
      prevItems.map((item, index) => {
        if (item.minValue !== undefined && item.maxValue !== undefined) {
          const newLabel = generateLabel(
            item.minValue,
            item.maxValue,
            index === 0,
            index === prevItems.length - 1,
            prevItems.length
          );
          return { ...item, label: newLabel };
        }
        return item;
      })
    );
  }, [prefix, suffix, decimals, legendType, legendItems.length]);

  // Handle legend item label changes
  const handleLegendItemChange = (id, newLabel) => {
    setLegendItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, label: newLabel } : item
      )
    );
  };

  // Handle legend item color changes
  const handleLegendItemColorChange = (id, newColor) => {
    setLegendItems((prev) =>
      prev.map((item) =>
        item.id === id ? { ...item, color: newColor } : item
      )
    );
  };

  // Reset to original values
  const handleReset = () => {
    if (
      window.confirm(
        "Are you sure you want to reset all changes? This cannot be undone."
      )
    ) {
      setLegendTitle(originalValuesRef.current.title);
      setColorLabel(originalValuesRef.current.colorLabel);
      setSizeLabel(originalValuesRef.current.sizeLabel);
      setPrefix(originalValuesRef.current.prefix);
      setSuffix(originalValuesRef.current.suffix);
      setDecimals(originalValuesRef.current.decimals);
      setHideLegend(originalValuesRef.current.hideLegend);
      setLegendItems(
        JSON.parse(JSON.stringify(originalValuesRef.current.items))
      );
      setHasUnsavedChanges(false);
    }
  };

  // Ref to track last preview config to avoid duplicates
  const lastPreviewConfigRef = useRef(null);

  // Trigger live preview when legend properties change
  const triggerPreview = useCallback(() => {
    console.log('[LegendPropertiesEditor] triggerPreview executing', {
      hasOnPreview: !!onPreview,
      hasLayerConfig: !!layerConfig
    });
    if (!onPreview || !layerConfig) {
      console.log('[LegendPropertiesEditor] triggerPreview early return - missing props');
      return;
    }

    // Build preview config with current legend values
    const previewConfig = {
      ...layerConfig,
      title: legendTitle,
      // Also set colorLegendTitle for comp/1varmap maps (CustomLegend checks this first)
      colorLegendTitle: legendTitle,
      colorLabel: colorLabel,
      sizeLabel: sizeLabel,
      hideLegend: hideLegend,
      valueFormat: {
        ...layerConfig.valueFormat,
        prefix: prefix,
        suffix: suffix,
        decimals: parseInt(decimals, 10),
      },
      // Flag to indicate this is a legend-only preview - don't touch the map layer
      isLegendOnlyPreview: true,
    };

    // Helper to format values with current settings for preview
    const formatPreviewValue = (value, includeSuffix = true) => {
      if (value === undefined || value === null || typeof value !== "number")
        return value || "";
      const numValue = Number(value);
      if (isNaN(numValue)) return value;
      let formattedValue = "";
      const dec = parseInt(decimals, 10);
      if (dec === 0) {
        formattedValue = Math.round(numValue).toLocaleString();
      } else {
        formattedValue = numValue.toFixed(dec);
      }
      return `${prefix}${formattedValue}${includeSuffix && suffix ? suffix : ''}`;
    };

    // Update labels in the appropriate config section based on legend type
    // Use manually edited labels from legendItems if available
    if (legendType === "class-breaks" && previewConfig.classBreakInfos) {
      previewConfig.classBreakInfos = previewConfig.classBreakInfos.map(
        (breakInfo, index) => {
          // First check for manually edited label in legendItems
          const item = legendItems.find((i) => i.id === index);
          if (item) {
            return { ...breakInfo, label: item.label };
          }
          return breakInfo;
        }
      );
    } else if (legendType === "custom-dual" && previewConfig.colorClassBreakInfos) {
      previewConfig.colorClassBreakInfos = previewConfig.colorClassBreakInfos.map(
        (breakInfo, index) => {
          // First check for manually edited label in legendItems
          const item = legendItems.find((i) => i.id === index);
          if (item) {
            return { ...breakInfo, label: item.label };
          }
          return breakInfo;
        }
      );
    }

    // Avoid duplicate previews with same config
    const configKey = JSON.stringify({
      title: previewConfig.title,
      hideLegend: previewConfig.hideLegend,
      valueFormat: previewConfig.valueFormat,
      legendItems: legendItems.map(i => ({ id: i.id, label: i.label })),
    });
    if (lastPreviewConfigRef.current === configKey) {
      console.log('[LegendPropertiesEditor] Skipping duplicate preview');
      return;
    }
    lastPreviewConfigRef.current = configKey;

    console.log('[LegendPropertiesEditor] Triggering live preview');
    onPreview(previewConfig);
  }, [onPreview, layerConfig, legendTitle, colorLabel, sizeLabel, hideLegend, prefix, suffix, decimals, legendItems, legendType]);

  // Trigger preview when any legend property changes
  // Use a separate debounce ref to avoid resetting timer on every triggerPreview recreation
  const previewDebounceRef = useRef(null);

  useEffect(() => {
    console.log('[LegendPropertiesEditor] Preview effect running', { hasUnsavedChanges });
    if (hasUnsavedChanges && onPreview && layerConfig) {
      // Clear any existing debounce timer
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }

      // Set a new debounce timer
      previewDebounceRef.current = setTimeout(() => {
        console.log('[LegendPropertiesEditor] Debounce completed, calling triggerPreview');
        triggerPreview();
      }, 300); // 300ms debounce
    }

    return () => {
      if (previewDebounceRef.current) {
        clearTimeout(previewDebounceRef.current);
      }
    };
  }, [hasUnsavedChanges, legendTitle, prefix, suffix, decimals, hideLegend, legendItems]); // Include legendItems for manual label edits

  const handleSave = async () => {
    if (!layerConfig) {
      console.warn("Cannot save: No layer configuration available");
      return;
    }

    setIsSaving(true);

    try {
      // Create updated config with new legend properties
      const updatedConfig = {
        ...layerConfig,
        title: legendTitle,
        // Also set colorLegendTitle for comp/1varmap maps (CustomLegend checks this first)
        colorLegendTitle: legendTitle,
        colorLabel: colorLabel,
        sizeLabel: sizeLabel,
        hideLegend: hideLegend,
        valueFormat: {
          ...layerConfig.valueFormat,
          prefix: prefix,
          suffix: suffix,
          decimals: parseInt(decimals, 10),
        },
      };

      // Update labels in the appropriate config section based on legend type
      // Use the manually edited labels from legendItems directly
      if (legendType === "class-breaks" && updatedConfig.classBreakInfos) {
        updatedConfig.classBreakInfos = updatedConfig.classBreakInfos.map(
          (breakInfo, index) => {
            const item = legendItems.find((i) => i.id === index);
            // Use the edited label directly - user's manual edits take precedence
            if (item) {
              return { ...breakInfo, label: item.label };
            }
            return breakInfo;
          }
        );
      } else if (legendType === "pipeline" && updatedConfig.statusColors) {
        // Update pipeline status colors from edited legend items
        const updatedStatusColors = { ...updatedConfig.statusColors };
        legendItems.forEach((item) => {
          // Find the original status name (label is the status name)
          const statusName = item.label;
          if (updatedStatusColors.hasOwnProperty(statusName)) {
            updatedStatusColors[statusName] = item.color;
          }
        });
        updatedConfig.statusColors = updatedStatusColors;
      } else if (
        legendType === "custom-dual" &&
        updatedConfig.colorClassBreakInfos
      ) {
        updatedConfig.colorClassBreakInfos =
          updatedConfig.colorClassBreakInfos.map((breakInfo, index) => {
            const item = legendItems.find((i) => i.id === index);
            // Use the edited label directly - user's manual edits take precedence
            if (item) {
              return { ...breakInfo, label: item.label };
            }
            return breakInfo;
          });
      } else if (legendType === "dot-density" && updatedConfig.attributes) {
        updatedConfig.attributes = updatedConfig.attributes.map(
          (attr, index) => {
            const item = legendItems.find((i) => i.id === index);
            if (item) {
              return { ...attr, label: item.label };
            }
            return attr;
          }
        );
      }

      // Call onConfigChange to update the configuration
      if (onConfigChange) {
        onConfigChange(updatedConfig);
      }

      // Call onSave if provided (for additional save logic)
      if (onSave) {
        await onSave(updatedConfig);
      }

      // Update original values
      originalValuesRef.current = {
        title: legendTitle,
        colorLabel: colorLabel,
        sizeLabel: sizeLabel,
        prefix: prefix,
        suffix: suffix,
        decimals: decimals,
        hideLegend: hideLegend,
        items: JSON.parse(JSON.stringify(legendItems)),
      };
      setHasUnsavedChanges(false);

      // CRITICAL: Trigger final preview with saved config BEFORE closing
      // This ensures customLegendContent is updated with the final saved configuration
      // so the actual legend reflects the saved changes, not stale preview data
      if (onPreview) {
        console.log('[LegendPropertiesEditor] Triggering final preview before close with saved config');
        onPreview(updatedConfig);
      }

      // Close the editor
      onClose();
    } catch (error) {
      console.error("Error saving legend properties:", error);
      alert("Failed to save legend properties. Check console for details.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    if (hasUnsavedChanges) {
      if (
        window.confirm(
          "You have unsaved changes. Are you sure you want to close?"
        )
      ) {
        if (onClose) onClose();
      }
    } else {
      if (onClose) onClose();
    }
  };

  // Get color string from various formats
  const getColorString = (color) => {
    if (!color) return "#808080";
    if (typeof color === "string") return color;
    if (Array.isArray(color) && color.length >= 3) {
      const [r, g, b, a = 1] = color;
      return `rgba(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)}, ${a})`;
    }
    return "#808080";
  };

  return (
    <div
      className={`
      fixed inset-y-0 right-0 z-30 w-[500px] h-full flex flex-col bg-white dark:bg-gray-800 shadow-xl
      transform transition-transform duration-300 ease-in-out border-l border-gray-200 dark:border-gray-700
      ${isOpen ? "translate-x-0" : "translate-x-full"}
    `}
    >
      {/* Header */}
      <div className="flex-none h-16 flex items-center justify-between px-6 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center space-x-2">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-100">
            Edit Legend
          </h2>
          {hasUnsavedChanges && (
            <span className="text-xs bg-yellow-500 text-white px-2 py-1 rounded">
              Unsaved
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-6">
          {/* Legend Visibility Section */}
          <div className="space-y-4 border-b border-gray-200 dark:border-gray-700 pb-6">
            <div
              className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              onClick={() => setHideLegend(!hideLegend)}
            >
              <div className="flex items-center space-x-3">
                {hideLegend ? (
                  <EyeOff size={20} className="text-gray-500 dark:text-gray-400" />
                ) : (
                  <Eye size={20} className="text-blue-500" />
                )}
                <div>
                  <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {hideLegend ? 'Legend Hidden' : 'Legend Visible'}
                  </span>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {hideLegend ? 'Legend will not appear on the map' : 'Legend is displayed on the map'}
                  </p>
                </div>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={!hideLegend}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  hideLegend ? 'bg-gray-300 dark:bg-gray-600' : 'bg-blue-600'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  setHideLegend(!hideLegend);
                }}
              >
                <span
                  aria-hidden="true"
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    hideLegend ? 'translate-x-0' : 'translate-x-5'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Legend Title Section */}
          <div className="space-y-4 border-b border-gray-200 dark:border-gray-700 pb-6">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label
                  htmlFor="legend-title"
                  className="block text-sm font-medium text-gray-700 dark:text-gray-300"
                >
                  Legend Title
                </label>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  Main heading
                </span>
              </div>
              <input
                id="legend-title"
                type="text"
                value={legendTitle}
                onChange={(e) => setLegendTitle(e.target.value)}
                placeholder="Enter legend title"
                disabled={hideLegend}
                className={`w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ${
                  hideLegend ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              />
            </div>

            {/* Color and Size Labels for 2 Variable Maps */}
            {is2VariableMap && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="color-label"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Color Label
                  </label>
                  <input
                    id="color-label"
                    type="text"
                    value={colorLabel}
                    onChange={(e) => setColorLabel(e.target.value)}
                    placeholder="e.g., AvgBasePSF"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Label for color variable
                  </p>
                </div>

                <div>
                  <label
                    htmlFor="size-label"
                    className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                  >
                    Size Label
                  </label>
                  <input
                    id="size-label"
                    type="text"
                    value={sizeLabel}
                    onChange={(e) => setSizeLabel(e.target.value)}
                    placeholder="e.g., TotalUnits"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Label for size variable
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Value Format Section */}
          <div className="space-y-4 border-b border-gray-200 dark:border-gray-700 pb-6">
            <div className="flex items-center justify-between">
              <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
                Value Format
              </h3>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                For numbers in legend
              </span>
            </div>

            {/* Prefix */}
            <div>
              <label
                htmlFor="format-prefix"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Prefix
              </label>
              <input
                id="format-prefix"
                type="text"
                value={prefix}
                onChange={(e) => setPrefix(e.target.value)}
                placeholder="e.g., $"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Text before values (e.g., &quot;$&quot; for currency)
              </p>
            </div>

            {/* Suffix */}
            <div>
              <label
                htmlFor="format-suffix"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Suffix
              </label>
              <input
                id="format-suffix"
                type="text"
                value={suffix}
                onChange={(e) => setSuffix(e.target.value)}
                placeholder="e.g., %, /sq mi"
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Text after values (e.g., &quot;%&quot;, &quot;/sq mi&quot;)
              </p>
            </div>

            {/* Decimal Places */}
            <div>
              <label
                htmlFor="format-decimals"
                className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
              >
                Decimal Places
              </label>
              <input
                id="format-decimals"
                type="number"
                min="0"
                max="10"
                value={decimals}
                onChange={(e) => setDecimals(parseInt(e.target.value, 10))}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Number of decimal places (0-10)
              </p>
            </div>

            {/* Format Preview */}
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-md">
              <p className="text-xs font-medium text-blue-900 dark:text-blue-300 mb-1">
                Format Preview
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Single: {formatValue(1234.57)}
              </p>
              <p className="text-sm text-blue-800 dark:text-blue-200">
                Range: {formatValue(1234.57, false)} - {formatValue(5678.90, true)}
              </p>
            </div>
          </div>

          {/* Legend Items Section */}
          {legendItems.length > 0 && (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
                  Legend Items
                </h3>
                <span className="text-xs text-gray-500 dark:text-gray-400">
                  {legendItems.length} items
                </span>
              </div>

              <div className="space-y-3">
                {legendItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-start space-x-3 p-3 bg-gray-50 dark:bg-gray-700/50 rounded-md"
                  >
                    {/* Color picker for pipeline legends */}
                    {legendType === "pipeline" ? (
                      <input
                        type="color"
                        value={getColorString(item.color)}
                        onChange={(e) =>
                          handleLegendItemColorChange(item.id, e.target.value)
                        }
                        className="w-10 h-10 rounded flex-shrink-0 cursor-pointer border border-gray-300 dark:border-gray-600"
                        title="Click to change color"
                      />
                    ) : (
                      /* Color indicator (non-editable) */
                      <div
                        className="w-6 h-6 rounded flex-shrink-0 mt-1 border border-gray-300 dark:border-gray-600"
                        style={{ backgroundColor: getColorString(item.color) }}
                      />
                    )}

                    {/* Label editor */}
                    <div className="flex-1">
                      <input
                        type="text"
                        value={item.label}
                        onChange={(e) =>
                          handleLegendItemChange(item.id, e.target.value)
                        }
                        disabled={!item.editable || legendType === "pipeline"}
                        className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      />
                      {item.minValue !== undefined &&
                        item.maxValue !== undefined && (
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            Range: {formatValue(item.minValue)} -{" "}
                            {formatValue(item.maxValue)}
                          </p>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Preview Section */}
          <div className="space-y-3 border-t border-gray-200 dark:border-gray-700 pt-6">
            <h3 className="text-md font-semibold text-gray-800 dark:text-gray-100">
              Legend Preview
            </h3>
            <div className="bg-white border border-gray-300 rounded-md shadow-md p-4 max-w-xs">
              <h4 className="text-sm font-semibold mb-2 text-black">
                {legendTitle || "(No title)"}
              </h4>
              <div className="space-y-2">
                {legendItems.slice(0, 5).map((item) => (
                  <div key={item.id} className="flex items-center">
                    <div
                      className="w-4 h-4 mr-2 rounded flex-shrink-0 border border-gray-400"
                      style={{ backgroundColor: getColorString(item.color) }}
                    />
                    <span className="text-xs text-black truncate">
                      {item.label}
                    </span>
                  </div>
                ))}
                {legendItems.length > 5 && (
                  <p className="text-xs text-gray-500 italic">
                    ... and {legendItems.length - 5} more items
                  </p>
                )}
                {legendItems.length === 0 && (
                  <p className="text-xs text-gray-500 italic">
                    No legend items available
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="flex-none p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
        <div className="flex justify-between items-center space-x-3">
          {/* Reset Button */}
          <button
            onClick={handleReset}
            disabled={!hasUnsavedChanges}
            className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            title="Reset all changes to original values"
          >
            <RotateCcw size={16} />
            <span>Reset</span>
          </button>

          <div className="flex space-x-3">
            {/* Cancel Button */}
            <button
              onClick={handleClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              Cancel
            </button>

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={isSaving || !hasUnsavedChanges}
              className="flex items-center justify-center space-x-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Save size={16} className={isSaving ? "animate-spin" : ""} />
              <span>{isSaving ? "Saving..." : "Apply Changes"}</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

LegendPropertiesEditor.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  layerConfig: PropTypes.object,
  onConfigChange: PropTypes.func,
  onSave: PropTypes.func,
  onPreview: PropTypes.func,
};

export default LegendPropertiesEditor;
